import type { ReactNode } from 'react';
import type { ProductPreviewContent } from '../../model/product-preview-content';
import {
  Check,
  CircleCheck,
  Copy,
  DollarSign,
  ImageOff,
  Star,
  X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { htmlToPlainText } from '@/shared/lib/copy-with-formatting';
import { joinCss } from '@/shared/lib/css-utils';
import { sanitizeHtml } from '@/shared/lib/sanitize-html';
import { Button } from '@/shared/ui/button';
import { ImageLightbox } from '@/shared/ui/image-lightbox';
import { Modal } from '@/shared/ui/modal';
import { Text } from '@/shared/ui/text';
import { playableVideo } from '../../model/product-preview-content';
import css from './product-detail-modal.module.scss';

// The product window's shared pieces: the header and the Preview tab. XT's
// ProductDetailModal adds its internal tabs around them; ProductPreviewModal
// is the Preview alone (the agent portal). Deep imports only, so the portal
// can bundle this without XT's barrels.

const COPIED_MS = 1500;

type HeaderProps = {
  title: string;
  stars: number;
  budgetTier: number;
  onClose: () => void;
};

/** Title with its star and budget ratings, and the close button. */
export const ProductPreviewHeader = ({
  title,
  stars,
  budgetTier,
  onClose,
}: HeaderProps) => (
  <div className={css.header}>
    <div className={css.titleRow}>
      <Text className={css.title} weight="medium">
        {title}
      </Text>
      {stars > 0 && (
        <span aria-label={`${stars} stars`} className={css.rating} role="img">
          {Array.from({ length: 5 }, (_, i) => (
            <Star
              className={i < stars ? css.starOn : css.starOff}
              fill={i < stars ? 'currentColor' : 'none'}
              key={`star-${i}`}
              size={18}
            />
          ))}
        </span>
      )}
      {budgetTier > 0 && (
        <span
          aria-label={`Budget: ${'$'.repeat(budgetTier)}`}
          className={css.rating}
          role="img"
        >
          {Array.from({ length: 4 }, (_, i) => (
            <DollarSign
              className={i < budgetTier ? css.budgetOn : css.budgetOff}
              key={`budget-${i}`}
              size={16}
            />
          ))}
        </span>
      )}
    </div>
    {/* Distinct from the footer's "Close" so the two controls are
        individually addressable to assistive tech. */}
    <button
      aria-label="Close preview"
      className={css.close}
      onClick={onClose}
      type="button"
    >
      <X size={18} />
    </button>
  </div>
);

type SectionsProps = {
  content: ProductPreviewContent;
  /** Opens the lightbox at an image. */
  onOpenImage: (index: number) => void;
  onDescriptionCopied?: () => void;
};

/** Gallery, description, public notes and facilities. */
export const ProductPreviewSections = ({
  content,
  onOpenImage,
  onDescriptionCopied,
}: SectionsProps) => {
  const { title, images, facilities } = content;
  const descriptionHtml = sanitizeHtml(content.descriptionHtml);
  const descriptionText = htmlToPlainText(content.descriptionHtml);
  // Public (customer-facing) notes — the same field the "Public notes" edit
  // tab writes, shown between Description and Facilities.
  const notesHtml = sanitizeHtml(content.notesHtml);
  const useThumbColumn = images.length >= 3;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), COPIED_MS);
    return () => clearTimeout(timer);
  }, [copied]);

  const onCopyDescription = () => {
    // Description is rich-text (tiptap) HTML — copy readable plain text so
    // paste targets don't get raw `<p>`/`<span>` markup.
    if (!descriptionText) return;
    navigator.clipboard?.writeText(descriptionText);
    setCopied(true);
    onDescriptionCopied?.();
  };

  // A product with a video opens on it, playing, instead of its photos.
  const video = content.videos[0] ? playableVideo(content.videos[0]) : null;

  return (
    <>
      {video ? (
        <div className={css.video}>
          {video.kind === 'file' ? (
            <video
              aria-label={`${title} video`}
              autoPlay
              className={css.videoMedia}
              controls
              loop
              muted
              playsInline
              src={video.src}
            />
          ) : (
            <iframe
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              className={css.videoMedia}
              src={video.src}
              title={`${title} video`}
            />
          )}
        </div>
      ) : null}
      {!video && images.length > 0 && (
        <div
          className={joinCss(
            css.gallery,
            useThumbColumn ? css.galleryThree : false
          )}
        >
          <button
            className={css.heroBtn}
            onClick={() => onOpenImage(0)}
            type="button"
          >
            <img alt={title} className={css.heroImg} src={images[0]} />
          </button>
          {useThumbColumn && (
            <div className={css.thumbCol}>
              {images.slice(1, 3).map((img, index) => (
                <button
                  className={css.thumbBtn}
                  key={img}
                  onClick={() => onOpenImage(index + 1)}
                  type="button"
                >
                  <img
                    alt={`${title} ${index + 2}`}
                    className={css.thumbImg}
                    src={img}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
      {!video && images.length === 0 && (
        <div className={css.noImage}>
          <ImageOff size={40} />
        </div>
      )}

      <section className={css.section}>
        <div className={css.sectionHead}>
          <h3 className={css.sectionTitle}>Description</h3>
          {descriptionText && (
            <button
              aria-label="Copy description"
              className={css.copyButton}
              onClick={onCopyDescription}
              title="Copy description"
              type="button"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          )}
        </div>
        {descriptionHtml ? (
          <div
            className={css.descriptionHtml}
            dangerouslySetInnerHTML={{ __html: descriptionHtml }}
          />
        ) : (
          <Text variant="muted">—</Text>
        )}
      </section>

      {notesHtml && (
        <section className={css.section}>
          <h3 className={css.sectionTitle}>Public notes</h3>
          <div
            className={css.descriptionHtml}
            dangerouslySetInnerHTML={{ __html: notesHtml }}
          />
        </section>
      )}

      {facilities.length > 0 && (
        <section className={css.section}>
          <h3 className={css.sectionTitle}>Facilities</h3>
          <div className={css.facilitiesGrid}>
            {facilities.map((facility) => (
              <div className={css.facilityItem} key={facility.id}>
                <CircleCheck className={css.facilityIcon} size={18} />
                <span>{facility.title}</span>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
};

type ProductPreviewModalProps = {
  /** Closed while false. */
  open: boolean;
  content: ProductPreviewContent | undefined;
  isLoading: boolean;
  isError: boolean;
  onClose: () => void;
  /** Contextual action beside Close (e.g. the planner's Add). */
  primaryAction?: ReactNode;
  /** Extra class on the window, e.g. a token scope for another app. */
  className?: string;
};

/** The product window's Preview alone: no internal tabs or supplier data. */
export const ProductPreviewModal = ({
  open,
  content,
  isLoading,
  isError,
  onClose,
  primaryAction,
  className,
}: ProductPreviewModalProps) => {
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const title = isLoading ? 'Product' : (content?.title ?? 'Product');

  return (
    <>
      <Modal
        className={joinCss(css.modal, className)}
        onClose={onClose}
        open={open}
      >
        <ProductPreviewHeader
          budgetTier={content?.budgetTier ?? 0}
          onClose={onClose}
          stars={content?.stars ?? 0}
          title={title}
        />
        <div className={css.body}>
          {isError && (
            <Text variant="error">
              Failed to load product details. It may have been deleted.
            </Text>
          )}
          {content ? (
            <ProductPreviewSections
              content={content}
              onOpenImage={setLightboxIndex}
            />
          ) : null}
        </div>
        <div className={css.footer}>
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
          {primaryAction}
        </div>
      </Modal>
      <ImageLightbox
        images={content?.images ?? []}
        onClose={() => setLightboxIndex(null)}
        startIndex={open ? lightboxIndex : null}
        title={title}
      />
    </>
  );
};
